import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Upload, Search, Trash2, Edit2, Download, AlertCircle, Layers, Check, X, FileSpreadsheet, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FUNNEL_STAGES = ['awareness', 'conversion'];

export default function FunnelMapping({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mappings, setMappings] = useState([]);
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  // When the user lands here from the Funnel Split unmapped banner, this set holds
  // the campaign names to focus on. Null = no focus filter active.
  const [focusedCampaigns, setFocusedCampaigns] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]); // row-level error report
  
  // Form states
  const [newCampaign, setNewCampaign] = useState('');
  const [newFunnelStage, setNewFunnelStage] = useState('awareness');
  const [newSpendsAw, setNewSpendsAw] = useState(100);
  const [newSpendsCv, setNewSpendsCv] = useState(0);
  const [newSalesAw, setNewSalesAw] = useState(100);
  const [newSalesCv, setNewSalesCv] = useState(0);

  // Edit row state — stores in-flight values for the row being edited
  const [editForm, setEditForm] = useState(null); // {funnel_stage, spends_aw, spends_cv, sales_aw, sales_cv}

  // Bulk mapping states
  const [allCampaigns, setAllCampaigns] = useState([]);
  // Enriched campaign metadata [{campaign_name, channel, ad_type, ad_product}]
  // — used to render Channel / Ad Type / Ad Product columns in the mapping
  // table and to power the two new filters (channel + ad_type).
  const [campaignsMeta, setCampaignsMeta] = useState([]);
  const [filterChannel, setFilterChannel] = useState('all');
  const [filterAdType, setFilterAdType] = useState('all');
  const [mappedCampaigns, setMappedCampaigns] = useState(new Set());
  const [bulkStage, setBulkStage] = useState('awareness');
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkOverwrite, setBulkOverwrite] = useState(false);

  // Download Data feature — export mappings as multi-sheet XLSX.
  const [showExportPopover, setShowExportPopover] = useState(false);
  const [exportScope, setExportScope] = useState('all'); // 'all' | 'filtered' | 'selected'
  const [exportChannels, setExportChannels] = useState(new Set()); // channels checked in the panel
  const [exporting, setExporting] = useState(false);

  // Distinct channel list — same source that powers the "All Channels" filter above.
  const availableChannels = useMemo(() => {
    const s = new Set();
    for (const c of campaignsMeta) if (c.channel) s.add(c.channel);
    return Array.from(s).sort();
  }, [campaignsMeta]);

  // Whenever the channel list changes (data first loads or new channel added),
  // default all channels to checked in the export panel.
  useEffect(() => {
    if (availableChannels.length && exportChannels.size === 0) {
      setExportChannels(new Set(availableChannels));
    }
  }, [availableChannels]);

  const toggleExportChannel = (ch) => {
    setExportChannels(prev => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch); else next.add(ch);
      return next;
    });
  };

  useEffect(() => {
    fetchMappings();
    fetchAllCampaigns();
  }, []);

  // Read the `?focus=unmapped` query param and pull the campaign list from sessionStorage
  // (set by FullFunnel's "Open Funnel Mapping →" button). When activated, also force
  // filterStage='unmapped' so the table is rendered from the pseudo-rows path below.
  useEffect(() => {
    if (searchParams.get('focus') !== 'unmapped') {
      setFocusedCampaigns(null);
      return;
    }
    try {
      const raw = sessionStorage.getItem('funnel-mapping:focus-unmapped');
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const list = Array.isArray(parsed?.campaigns) ? parsed.campaigns : [];
      if (list.length > 0) {
        setFocusedCampaigns(new Set(list));
        setFilterStage('unmapped');
      }
    } catch (e) { /* ignore parse errors */ }
  }, [searchParams]);

  const clearFocusFilter = () => {
    setFocusedCampaigns(null);
    sessionStorage.removeItem('funnel-mapping:focus-unmapped');
    // Drop the query param without forcing a re-mount
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
    setFilterStage('all');
  };

  useEffect(() => {
    filterMappings();
  }, [mappings, searchTerm, filterStage, filterChannel, filterAdType, allCampaigns, campaignsMeta, mappedCampaigns, focusedCampaigns]);

  const fetchAllCampaigns = async () => {
    try {
      const res = await fetch(`${API}/funnel-mappings/campaigns`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllCampaigns(data.campaigns || []);
        setCampaignsMeta(data.campaigns_meta || []);
        setMappedCampaigns(new Set(data.mapped || []));
      }
    } catch (e) { console.error(e); }
  };

  const fetchMappings = async () => {
    try {
      const response = await fetch(`${API}/funnel-mappings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setMappings(data);
      }
    } catch (error) {
      console.error('Error fetching mappings:', error);
      toast.error('Failed to load mappings');
    }
  };

  // Fast lookup by campaign_name → enrichment payload {channel, ad_type, ad_product}.
  // A single campaign can appear in multiple channel/ad_type combinations
  // (rare in practice) — we use the first hit for the mapping row context.
  const metaByName = useMemo(() => {
    const m = new Map();
    for (const c of campaignsMeta) {
      if (!m.has(c.campaign_name)) m.set(c.campaign_name, c);
    }
    return m;
  }, [campaignsMeta]);

  const applyChannelAdTypeFilters = (rows) => {
    let out = rows;
    if (filterChannel !== 'all') {
      out = out.filter(r => (metaByName.get(r.campaign_name)?.channel || '') === filterChannel);
    }
    if (filterAdType !== 'all') {
      out = out.filter(r => (metaByName.get(r.campaign_name)?.ad_type || '') === filterAdType);
    }
    return out;
  };

  const filterMappings = () => {
    if (filterStage === 'unmapped') {
      // Show campaigns present in uploaded data but missing a funnel stage assignment.
      // Build pseudo-rows so the table render below treats them like normal items.
      let unmappedNames = allCampaigns.filter(c => !mappedCampaigns.has(c));
      // When the user landed here from Funnel Split's unmapped-banner, narrow further
      // to ONLY the campaigns relevant to the date range that triggered the deep link.
      if (focusedCampaigns) {
        unmappedNames = unmappedNames.filter(c => focusedCampaigns.has(c));
      }
      let pseudo = unmappedNames.map(c => ({
        mapping_id: `unmapped_${c}`,
        campaign_name: c,
        funnel_stage: 'unmapped',
        updated_at: null,
      }));
      if (searchTerm) {
        pseudo = pseudo.filter(m => m.campaign_name.toLowerCase().includes(searchTerm.toLowerCase()));
      }
      pseudo = applyChannelAdTypeFilters(pseudo);
      setFilteredMappings(pseudo);
      return;
    }

    let filtered = [...mappings];
    if (focusedCampaigns) {
      filtered = filtered.filter(m => focusedCampaigns.has(m.campaign_name));
    }
    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.campaign_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStage !== 'all') {
      filtered = filtered.filter(m => m.funnel_stage === filterStage);
    }
    filtered = applyChannelAdTypeFilters(filtered);
    setFilteredMappings(filtered);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Reset input so re-selecting the same file still triggers onChange
    event.target.value = '';

    setUploading(true);
    setUploadErrors([]);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Uploads always upsert (update existing campaigns, insert new ones).
      // The old "overwrite" checkbox is overridden here per spec.
      const response = await fetch(`${API}/funnel-mappings/upload-file?overwrite=true`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.status === 400) {
        const err = await response.json().catch(() => ({}));
        const msg = typeof err.detail === 'string' ? err.detail : 'Invalid file format';
        setUploadErrors([{ row: '-', error: msg }]);
        toast.error(msg);
        return;
      }
      if (!response.ok) throw new Error('Upload failed');

      const result = await response.json();
      const rowErrors = Array.isArray(result.errors) ? result.errors : [];

      if (result.created + result.updated > 0) {
        toast.success(`Created: ${result.created}, Updated: ${result.updated}${rowErrors.length ? `, Skipped: ${rowErrors.length} invalid row(s)` : ''}`);
      } else if (rowErrors.length > 0) {
        toast.error('All rows failed validation — see details below');
      } else {
        toast.message('No rows found in file');
      }

      setUploadErrors(rowErrors);
      fetchMappings();
      // Close dialog only when the whole file imported cleanly
      if (rowErrors.length === 0) setShowUploadDialog(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
      setUploadErrors([{ row: '-', error: error.message || 'Upload failed' }]);
    } finally {
      setUploading(false);
    }
  };

  // Default split helper — when stage changes, reset splits to 100/0 or 0/100
  const defaultSplitsForStage = (stage) => stage === 'awareness'
    ? { spends_aw: 100, spends_cv: 0, sales_aw: 100, sales_cv: 0 }
    : { spends_aw: 0, spends_cv: 100, sales_aw: 0, sales_cv: 100 };

  const handleStageChangeInAdd = (s) => {
    setNewFunnelStage(s);
    const d = defaultSplitsForStage(s);
    setNewSpendsAw(d.spends_aw); setNewSpendsCv(d.spends_cv);
    setNewSalesAw(d.sales_aw); setNewSalesCv(d.sales_cv);
  };

  const handleAddMapping = async () => {
    if (!newCampaign.trim()) {
      toast.error('Please enter a campaign name');
      return;
    }
    if (Math.abs((+newSpendsAw + +newSpendsCv) - 100) > 0.01) {
      toast.error('Spends split must sum to 100');
      return;
    }
    if (Math.abs((+newSalesAw + +newSalesCv) - 100) > 0.01) {
      toast.error('Sales split must sum to 100');
      return;
    }

    try {
      const response = await fetch(`${API}/funnel-mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          campaign_name: newCampaign,
          funnel_stage: newFunnelStage,
          spends_awareness_pct: +newSpendsAw,
          spends_conversion_pct: +newSpendsCv,
          sales_awareness_pct: +newSalesAw,
          sales_conversion_pct: +newSalesCv,
        })
      });

      if (response.status === 409) {
        toast.error('Mapping already exists. Use edit to update.');
        return;
      }
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create mapping');
      }

      toast.success('Mapping created successfully');
      fetchMappings();
      fetchAllCampaigns();
      setNewCampaign('');
      handleStageChangeInAdd('awareness');
      setShowAddDialog(false);
    } catch (error) {
      console.error('Add mapping error:', error);
      toast.error(error.message || 'Failed to create mapping');
    }
  };

  // Begin editing a row — seed the form with current values
  const startEditMapping = (mapping) => {
    setEditingMapping(mapping.mapping_id);
    setEditForm({
      funnel_stage: mapping.funnel_stage,
      spends_aw: mapping.spends_awareness_pct ?? 0,
      spends_cv: mapping.spends_conversion_pct ?? 0,
      sales_aw: mapping.sales_awareness_pct ?? 0,
      sales_cv: mapping.sales_conversion_pct ?? 0,
    });
  };

  // Auto-fill complementary pair as user types
  const updateEditField = (key, value) => {
    const v = value === '' ? '' : Number(value);
    setEditForm(prev => {
      const next = { ...prev, [key]: v };
      if (key === 'spends_aw') next.spends_cv = v === '' ? '' : Math.max(0, Math.min(100, 100 - v));
      else if (key === 'spends_cv') next.spends_aw = v === '' ? '' : Math.max(0, Math.min(100, 100 - v));
      else if (key === 'sales_aw') next.sales_cv = v === '' ? '' : Math.max(0, Math.min(100, 100 - v));
      else if (key === 'sales_cv') next.sales_aw = v === '' ? '' : Math.max(0, Math.min(100, 100 - v));
      return next;
    });
  };
  const updateEditStage = (stage) => {
    const d = defaultSplitsForStage(stage);
    setEditForm({ funnel_stage: stage, ...d });
  };

  const saveEditMapping = async (mappingId) => {
    if (!editForm) return;
    if (Math.abs((+editForm.spends_aw + +editForm.spends_cv) - 100) > 0.01) {
      toast.error('Spends split must sum to 100'); return;
    }
    if (Math.abs((+editForm.sales_aw + +editForm.sales_cv) - 100) > 0.01) {
      toast.error('Sales split must sum to 100'); return;
    }
    try {
      const response = await fetch(`${API}/funnel-mappings/${mappingId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          funnel_stage: editForm.funnel_stage,
          spends_awareness_pct: +editForm.spends_aw,
          spends_conversion_pct: +editForm.spends_cv,
          sales_awareness_pct: +editForm.sales_aw,
          sales_conversion_pct: +editForm.sales_cv,
        })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update');
      }
      toast.success('Mapping updated');
      fetchMappings();
      fetchAllCampaigns();
      setEditingMapping(null);
      setEditForm(null);
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update mapping');
    }
  };

  const handleUpdateMapping = saveEditMapping; // legacy alias not used now

  const handleDeleteMapping = async (mappingId) => {
    if (!confirm('Are you sure you want to delete this mapping?')) return;

    try {
      const response = await fetch(`${API}/funnel-mappings/${mappingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success('Mapping deleted');
      fetchMappings();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete mapping');
    }
  };

  const openBulkDialog = async () => {
    setShowBulkDialog(true);
    setBulkSearch('');
    setBulkSelected(new Set());
    setBulkOverwrite(false);
    try {
      const res = await fetch(`${API}/funnel-mappings/campaigns`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllCampaigns(data.campaigns || []);
        setMappedCampaigns(new Set(data.mapped || []));
      }
    } catch (e) { console.error(e); }
  };

  const toggleBulkSelect = (campaign) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      next.has(campaign) ? next.delete(campaign) : next.add(campaign);
      return next;
    });
  };

  const selectAllVisible = () => {
    const visible = filteredBulkCampaigns;
    setBulkSelected(prev => {
      const next = new Set(prev);
      const allSelected = visible.every(c => next.has(c));
      if (allSelected) { visible.forEach(c => next.delete(c)); }
      else { visible.forEach(c => next.add(c)); }
      return next;
    });
  };

  const handleBulkMap = async () => {
    if (bulkSelected.size === 0) { toast.error('Select at least one campaign'); return; }
    setBulkLoading(true);
    try {
      const payload = {
        mappings: [...bulkSelected].map(c => ({ campaign_name: c, funnel_stage: bulkStage })),
        overwrite: bulkOverwrite
      };
      const res = await fetch(`${API}/funnel-mappings/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Created: ${result.created}, Updated: ${result.updated}, Skipped: ${result.skipped}`);
        fetchMappings();
        setShowBulkDialog(false);
      } else { toast.error('Bulk mapping failed'); }
    } catch (e) { toast.error('Bulk mapping failed'); }
    finally { setBulkLoading(false); }
  };

  const mapUnmappedCampaign = async (campaignName, stage) => {
    try {
      const res = await fetch(`${API}/funnel-mappings/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings: [{ campaign_name: campaignName, funnel_stage: stage }], overwrite: false })
      });
      if (res.ok) {
        toast.success(`Mapped "${campaignName}" → ${stage}`);
        fetchMappings();
        fetchAllCampaigns();
      } else { toast.error('Failed to map'); }
    } catch (e) { toast.error('Failed to map'); }
  };

  const filteredBulkCampaigns = allCampaigns.filter(c =>
    !bulkSearch || c.toLowerCase().includes(bulkSearch.toLowerCase())
  );

  const getFunnelColor = (stage) => {
    switch (stage) {
      case 'awareness': return 'bg-blue-100 text-blue-800';
      case 'consideration': return 'bg-yellow-100 text-yellow-800';
      case 'conversion': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Download Data — build a multi-sheet XLSX from the current mapping context.
   *
   * Scope resolution:
   *   'all'      → every campaign known to the system (mapped + unmapped)
   *   'filtered' → whatever is currently in `filteredMappings` (respects search
   *                + stage + channel + ad_type filters)
   *   'selected' → all campaigns whose channel is in `exportChannels`, further
   *                narrowed by an active search term (per spec: if selected +
   *                filters, apply both)
   *
   * For every candidate row we emit: Campaign Name / Channel / Ad Type /
   * Ad Product / Funnel Stage. Rows are then grouped by channel into one
   * worksheet per channel, prefixed by a Summary sheet with per-channel counts.
   */
  const handleExportData = async () => {
    if (exportChannels.size === 0) {
      toast.error('Select at least one channel to export');
      return;
    }
    setExporting(true);
    try {
      // 1. Build the master list of campaign names for the current scope.
      const stageFor = new Map(mappings.map(m => [m.campaign_name, m.funnel_stage]));
      let campaignNames = [];
      if (exportScope === 'filtered') {
        campaignNames = filteredMappings.map(r => r.campaign_name);
      } else {
        // 'all' or 'selected' — start from every campaign we know about.
        campaignNames = Array.from(new Set(allCampaigns));
      }

      // 2. Turn campaign names into export rows with enrichment.
      let rows = [];
      for (const name of campaignNames) {
        const meta = metaByName.get(name) || {};
        rows.push({
          'Campaign Name': name,
          'Channel': meta.channel || 'Unknown',
          'Ad Type': meta.ad_type || '',
          'Ad Product': meta.ad_product || '',
          'Funnel Stage': stageFor.get(name) || 'Unmapped',
        });
      }

      // 3. Apply channel-checklist scope + active search narrowing.
      if (exportScope === 'selected') {
        rows = rows.filter(r => exportChannels.has(r.Channel));
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          rows = rows.filter(r => r['Campaign Name'].toLowerCase().includes(q));
        }
      } else if (exportScope === 'all') {
        // Still respect the channel checklist as an intentional narrowing knob.
        rows = rows.filter(r => exportChannels.has(r.Channel));
      } else {
        // 'filtered' already respects search+stage+channel+adType; also drop
        // rows whose (auto-enriched) Channel isn't in the checklist so the
        // user can further narrow the file if they like.
        rows = rows.filter(r => exportChannels.has(r.Channel));
      }

      if (rows.length === 0) {
        toast.error('No campaigns match the selected scope');
        setExporting(false);
        return;
      }

      // 4. Group rows by channel and build the workbook.
      const wb = XLSX.utils.book_new();

      const grouped = {};
      for (const r of rows) {
        const ch = r.Channel || 'Unknown';
        (grouped[ch] = grouped[ch] || []).push(r);
      }

      // FIRST tab = flat "All Campaigns" sheet so the actual campaign data
      // is visible the moment the file opens (Excel/Sheets defaults to the
      // first tab). Contains every exported row across all selected channels
      // with the full 5-column schema.
      const allRowsSorted = [...rows].sort((a, b) => {
        const c = (a.Channel || '').localeCompare(b.Channel || '');
        if (c !== 0) return c;
        return (a['Campaign Name'] || '').localeCompare(b['Campaign Name'] || '');
      });
      const allSheet = XLSX.utils.json_to_sheet(allRowsSorted, {
        header: ['Campaign Name', 'Channel', 'Ad Type', 'Ad Product', 'Funnel Stage'],
      });
      allSheet['!cols'] = [{ wch: 48 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, allSheet, 'All Campaigns');

      // Summary sheet second.
      const summary = [
        ['Channel', 'Campaign Count'],
        ...Object.entries(grouped)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([ch, list]) => [ch, list.length]),
        ['Total', rows.length],
      ];
      const summarySheet = XLSX.utils.aoa_to_sheet(summary);
      summarySheet['!cols'] = [{ wch: 22 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

      // One sheet per channel.
      for (const ch of Object.keys(grouped).sort()) {
        const ws = XLSX.utils.json_to_sheet(grouped[ch], {
          header: ['Campaign Name', 'Channel', 'Ad Type', 'Ad Product', 'Funnel Stage'],
        });
        ws['!cols'] = [{ wch: 48 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 16 }];
        // Excel sheet names: max 31 chars, no [ ] : \ / ? *
        const safeName = ch.replace(/[[\]:\\/?*]/g, '').slice(0, 31) || 'Channel';
        XLSX.utils.book_append_sheet(wb, ws, safeName);
      }

      // 5. Filename: FunnelMapping_{channel-or-All}_{YYYY-MM-DD}.xlsx
      const today = new Date().toISOString().slice(0, 10);
      const channelKeys = Object.keys(grouped);
      const chLabel = channelKeys.length === 1
        ? channelKeys[0].replace(/\s+/g, '-')
        : 'All';
      const fileName = `FunnelMapping_${chLabel}_${today}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(`Exported ${rows.length} campaign${rows.length === 1 ? '' : 's'} across ${channelKeys.length} sheet${channelKeys.length === 1 ? '' : 's'}`);
      setShowExportPopover(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to generate export file');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="funnel-mapping" />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="sticky top-0 z-10 bg-white border-b border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0F0F0F]">Funnel Mapping</h1>
              <p className="text-sm text-gray-600 mt-1">Map campaign names to funnel stages for accurate classification</p>
            </div>
            <div className="flex gap-2">
              {/* Native <a> link with download attribute → most reliable cross-browser
                  download. The backend returns the CSV with Content-Disposition: attachment,
                  so Chrome/Safari/Firefox cannot silently block it. */}
              <a
                href={`${API}/funnel-mappings/template`}
                download="funnel_mapping_template.csv"
                data-testid="download-template-btn"
                className="inline-flex items-center justify-center gap-2 h-9 px-3 text-sm font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Template
              </a>

              {/* Download Data — export current mappings as a multi-sheet XLSX.
                  Opens a small panel with channel checklist + scope options. */}
              <Popover open={showExportPopover} onOpenChange={setShowExportPopover}>
                <PopoverTrigger asChild>
                  <button
                    data-testid="download-data-btn"
                    className="inline-flex items-center justify-center gap-2 h-9 px-3 text-sm font-medium border border-gray-200 rounded-md bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Download Data
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-4" data-testid="download-data-panel">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-[#0F0F0F]">Export mapping data</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">One worksheet per selected channel, plus a summary tab.</p>
                    </div>

                    {/* Scope options */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Scope</Label>
                      {[
                        { v: 'all', label: 'All mapped campaigns' },
                        { v: 'filtered', label: 'Only campaigns matching current filters/search' },
                        { v: 'selected', label: 'Selected channels only' },
                      ].map(opt => (
                        <label key={opt.v} className="flex items-start gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900" data-testid={`export-scope-${opt.v}`}>
                          <input
                            type="radio"
                            name="export-scope"
                            checked={exportScope === opt.v}
                            onChange={() => setExportScope(opt.v)}
                            className="mt-0.5 accent-[#32B4AC]"
                          />
                          <span className="leading-snug">{opt.label}</span>
                        </label>
                      ))}
                    </div>

                    {/* Channel checklist */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <Label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Channels</Label>
                        <div className="flex gap-2 text-[11px]">
                          <button
                            type="button"
                            onClick={() => setExportChannels(new Set(availableChannels))}
                            className="text-[#32B4AC] hover:text-[#2a9d95]"
                            data-testid="export-select-all-channels"
                          >
                            Select all
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => setExportChannels(new Set())}
                            className="text-gray-500 hover:text-gray-700"
                            data-testid="export-clear-channels"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 p-2 space-y-1.5 bg-gray-50/50">
                        {availableChannels.length === 0 ? (
                          <p className="text-xs text-gray-500 italic">No channels available</p>
                        ) : availableChannels.map(ch => (
                          <label key={ch} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 hover:text-gray-900">
                            <input
                              type="checkbox"
                              checked={exportChannels.has(ch)}
                              onChange={() => toggleExportChannel(ch)}
                              className="accent-[#32B4AC]"
                              data-testid={`export-channel-${ch}`}
                            />
                            <span>{ch}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <Button
                      onClick={handleExportData}
                      disabled={exporting || exportChannels.size === 0}
                      className="w-full bg-[#32B4AC] hover:bg-[#2a9d95] disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="export-to-excel-btn"
                    >
                      {exporting ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                      ) : (
                        <><FileSpreadsheet className="w-4 h-4 mr-2" /> Export to Excel</>
                      )}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              
              <Dialog open={showUploadDialog} onOpenChange={(v) => { setShowUploadDialog(v); if (!v) setUploadErrors([]); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#32B4AC] hover:bg-[#2a9d95]">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Mapping File
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Upload Funnel Mapping File</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900 mb-2">File format (header row required):</p>
                      <code className="text-xs bg-white px-2 py-1 rounded block leading-relaxed">
                        campaign_name,funnel_stage,spends_awareness_%,spends_conversion_%,sales_awareness_%,sales_conversion_%<br/>
                        Wellcore_WP_PLA,Awareness,100,0,100,0<br/>
                        Wellcore_Bottle_ROI,Conversion,0,100,0,100<br/>
                        Wellcore_CrtnFL_ROI,Conversion,30,70,40,60
                      </code>
                      <p className="text-[11px] text-blue-700 mt-2">
                        Existing campaigns are updated automatically. New campaigns are inserted. Each split pair must sum to 100. Stage must be <strong>Awareness</strong> or <strong>Conversion</strong>.
                      </p>
                    </div>

                    <Button
                      onClick={() => document.getElementById('mapping-file-input').click()}
                      disabled={uploading}
                      className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]"
                      data-testid="upload-mapping-submit"
                    >
                      {uploading ? 'Uploading...' : 'Select File to Upload'}
                    </Button>
                    <input
                      id="mapping-file-input"
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileUpload}
                    />

                    {/* Row-level error report */}
                    {uploadErrors.length > 0 && (
                      <div className="border border-red-200 rounded-lg bg-red-50 overflow-hidden" data-testid="upload-errors">
                        <div className="px-3 py-2 border-b border-red-200 flex items-center justify-between">
                          <p className="text-xs font-semibold text-red-800">
                            {uploadErrors.length} row{uploadErrors.length > 1 ? 's' : ''} rejected — valid rows imported successfully
                          </p>
                          <button
                            onClick={() => setUploadErrors([])}
                            className="text-xs text-red-700 hover:text-red-900 underline"
                          >
                            Dismiss
                          </button>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-red-100 sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-1.5 font-semibold text-red-900 w-16">Row</th>
                                <th className="text-left px-3 py-1.5 font-semibold text-red-900">Issue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadErrors.map((e, i) => (
                                <tr key={i} className="border-t border-red-100">
                                  <td className="px-3 py-1.5 text-red-800 font-mono">{e.row ?? '-'}</td>
                                  <td className="px-3 py-1.5 text-red-700">{e.error}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="add-mapping-btn">
                    Add Mapping
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Mapping</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="campaign-name">Campaign Name</Label>
                      <Input
                        id="campaign-name"
                        value={newCampaign}
                        onChange={(e) => setNewCampaign(e.target.value)}
                        placeholder="e.g., WC_PLA_1"
                        data-testid="add-mapping-campaign-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="funnel-stage">Funnel Stage</Label>
                      <Select value={newFunnelStage} onValueChange={handleStageChangeInAdd}>
                        <SelectTrigger data-testid="add-mapping-stage-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FUNNEL_STAGES.map(stage => (
                            <SelectItem key={stage} value={stage} className="capitalize">
                              {stage}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-gray-400 mt-1">Stage is the primary classification. Splits default to 100/0 — adjust if a campaign contributes to both buckets.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                      <div>
                        <Label className="text-xs text-gray-600">Spends → Awareness %</Label>
                        <Input type="number" min="0" max="100" value={newSpendsAw}
                          onChange={(e) => { const v = +e.target.value; setNewSpendsAw(v); setNewSpendsCv(Math.max(0, Math.min(100, 100 - v))); }}
                          data-testid="add-mapping-spends-aw" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Spends → Conversion %</Label>
                        <Input type="number" min="0" max="100" value={newSpendsCv}
                          onChange={(e) => { const v = +e.target.value; setNewSpendsCv(v); setNewSpendsAw(Math.max(0, Math.min(100, 100 - v))); }}
                          data-testid="add-mapping-spends-cv" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Sales → Awareness %</Label>
                        <Input type="number" min="0" max="100" value={newSalesAw}
                          onChange={(e) => { const v = +e.target.value; setNewSalesAw(v); setNewSalesCv(Math.max(0, Math.min(100, 100 - v))); }}
                          data-testid="add-mapping-sales-aw" />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Sales → Conversion %</Label>
                        <Input type="number" min="0" max="100" value={newSalesCv}
                          onChange={(e) => { const v = +e.target.value; setNewSalesCv(v); setNewSalesAw(Math.max(0, Math.min(100, 100 - v))); }}
                          data-testid="add-mapping-sales-cv" />
                      </div>
                    </div>

                    <Button onClick={handleAddMapping} className="w-full bg-[#32B4AC] hover:bg-[#2a9d95]" data-testid="add-mapping-submit">
                      Add Mapping
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button size="sm" variant="outline" onClick={openBulkDialog} data-testid="bulk-map-btn">
                <Layers className="w-4 h-4 mr-2" />
                Bulk Map Campaigns
              </Button>
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Active focus filter — set when user lands here from the
                Funnel Split unmapped-campaigns banner. Lets them clear and view all. */}
            {focusedCampaigns && (
              <div
                className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center justify-between"
                data-testid="funnel-mapping-focus-banner"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span className="text-sm font-medium text-amber-900">
                    Showing {filteredMappings.length} unmapped campaign{filteredMappings.length === 1 ? '' : 's'}
                  </span>
                  <span className="text-xs text-amber-700">from your Funnel Split selection</span>
                </div>
                <button
                  type="button"
                  onClick={clearFocusFilter}
                  data-testid="funnel-mapping-clear-focus-btn"
                  className="text-xs font-semibold text-amber-900 underline hover:text-amber-950 whitespace-nowrap"
                >
                  Clear filter · Show all
                </button>
              </div>
            )}

            {/* Search and Filter — with Channel + Ad Type dropdowns so the user
                 can quickly focus on Meta / Google (or any other channel) campaigns. */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 relative min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search campaign names..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterChannel} onValueChange={setFilterChannel}>
                  <SelectTrigger className="w-44" data-testid="funnel-mapping-filter-channel">
                    <SelectValue placeholder="Channel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    {Array.from(new Set(campaignsMeta.map(c => c.channel).filter(Boolean))).sort().map(ch => (
                      <SelectItem key={ch} value={ch}>{ch}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterAdType} onValueChange={setFilterAdType}>
                  <SelectTrigger className="w-48" data-testid="funnel-mapping-filter-adtype">
                    <SelectValue placeholder="Ad Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ad Types</SelectItem>
                    {Array.from(new Set(
                      campaignsMeta
                        .filter(c => filterChannel === 'all' || c.channel === filterChannel)
                        .map(c => c.ad_type)
                        .filter(Boolean)
                    )).sort().map(at => (
                      <SelectItem key={at} value={at}>{at}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterStage} onValueChange={setFilterStage}>
                  <SelectTrigger className="w-44" data-testid="funnel-mapping-filter-stage">
                    <SelectValue placeholder="Filter by stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="unmapped" data-testid="funnel-mapping-filter-unmapped">
                      Unmapped only
                    </SelectItem>
                    {FUNNEL_STAGES.map(stage => (
                      <SelectItem key={stage} value={stage} className="capitalize">
                        {stage}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Mappings Table */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-[#0F0F0F]" data-testid="funnel-mapping-table-title">
                    {filterStage === 'unmapped' ? 'Unmapped Campaigns' : 'Campaign Mappings'} ({filteredMappings.length})
                  </h3>
                  {filterStage === 'unmapped' && filteredMappings.length > 0 && (
                    <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">{filteredMappings.length} campaign{filteredMappings.length === 1 ? '' : 's'} need a funnel stage</span>
                    </div>
                  )}
                  {filterStage !== 'unmapped' && mappings.length === 0 && (
                    <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 px-3 py-2 rounded-lg">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">No mappings configured yet</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="funnel-mapping-table">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-gray-700 font-semibold">Campaign Name</th>
                      <th className="text-left py-3 px-3 text-gray-700 font-semibold">Channel</th>
                      <th className="text-left py-3 px-3 text-gray-700 font-semibold">Ad Type</th>
                      <th className="text-left py-3 px-3 text-gray-700 font-semibold">Ad Product</th>
                      <th className="text-left py-3 px-3 text-gray-700 font-semibold">Funnel Stage</th>
                      <th className="text-right py-3 px-2 text-gray-700 font-semibold">Spends → Awareness %</th>
                      <th className="text-right py-3 px-2 text-gray-700 font-semibold">Spends → Conversion %</th>
                      <th className="text-right py-3 px-2 text-gray-700 font-semibold">Sales → Awareness %</th>
                      <th className="text-right py-3 px-2 text-gray-700 font-semibold">Sales → Conversion %</th>
                      <th className="text-left py-3 px-3 text-gray-700 font-semibold">Last Updated</th>
                      <th className="text-right py-3 px-3 text-gray-700 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMappings.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center py-8 text-gray-500">
                          {filterStage === 'unmapped'
                            ? (allCampaigns.length === 0
                                ? 'No campaigns uploaded yet. Upload data first.'
                                : (searchTerm ? 'No unmapped campaigns match your search.' : 'All campaigns are mapped.'))
                            : (searchTerm || filterStage !== 'all'
                                ? 'No mappings match your filters'
                                : 'No mappings available. Upload a file or add mappings manually.')}
                        </td>
                      </tr>
                    ) : (
                      filteredMappings.map(mapping => {
                        const isUnmapped = mapping.funnel_stage === 'unmapped';
                        const isEditing = editingMapping === mapping.mapping_id && editForm;
                        const cellNum = (val) => (val == null || val === '') ? '—' : `${(+val).toFixed(0)}%`;
                        // Look up the campaign's channel / ad_type / ad_product
                        // context from the enriched meta payload. Falls back to
                        // '—' when the mapping was added manually and doesn't
                        // exist in ad_properties_data yet.
                        const meta = metaByName.get(mapping.campaign_name);
                        return (
                        <tr key={mapping.mapping_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`funnel-mapping-row-${mapping.mapping_id}`}>
                          <td className="py-3 px-4 text-[#0F0F0F] font-medium">{mapping.campaign_name}</td>
                          <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{meta?.channel || '—'}</td>
                          <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{meta?.ad_type || '—'}</td>
                          <td className="py-3 px-3 text-gray-500 whitespace-nowrap text-xs" title={meta?.ad_product || ''}>
                            {meta?.ad_product ? (meta.ad_product.length > 40 ? meta.ad_product.slice(0, 40) + '…' : meta.ad_product) : '—'}
                          </td>

                          {isUnmapped ? (
                            <td colSpan="6" className="py-3 px-3">
                              <div className="flex items-center gap-3">
                                <Select value="" onValueChange={(val) => mapUnmappedCampaign(mapping.campaign_name, val)}>
                                  <SelectTrigger className="w-44" data-testid={`funnel-mapping-assign-${mapping.campaign_name}`}>
                                    <SelectValue placeholder="Assign stage…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FUNNEL_STAGES.map(stage => (
                                      <SelectItem key={stage} value={stage} className="capitalize">{stage}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <span className="text-xs text-gray-400">defaults to 100/0 split — adjust after assigning</span>
                              </div>
                            </td>
                          ) : isEditing ? (
                            <>
                              <td className="py-3 px-3">
                                <Select value={editForm.funnel_stage} onValueChange={updateEditStage}>
                                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {FUNNEL_STAGES.map(stage => (
                                      <SelectItem key={stage} value={stage} className="capitalize">{stage}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-3 px-2">
                                <Input type="number" min="0" max="100" value={editForm.spends_aw}
                                  onChange={e => updateEditField('spends_aw', e.target.value)}
                                  className="w-20 text-right tabular-nums"
                                  data-testid={`edit-spends-aw-${mapping.mapping_id}`} />
                              </td>
                              <td className="py-3 px-2">
                                <Input type="number" min="0" max="100" value={editForm.spends_cv}
                                  onChange={e => updateEditField('spends_cv', e.target.value)}
                                  className="w-20 text-right tabular-nums"
                                  data-testid={`edit-spends-cv-${mapping.mapping_id}`} />
                              </td>
                              <td className="py-3 px-2">
                                <Input type="number" min="0" max="100" value={editForm.sales_aw}
                                  onChange={e => updateEditField('sales_aw', e.target.value)}
                                  className="w-20 text-right tabular-nums"
                                  data-testid={`edit-sales-aw-${mapping.mapping_id}`} />
                              </td>
                              <td className="py-3 px-2">
                                <Input type="number" min="0" max="100" value={editForm.sales_cv}
                                  onChange={e => updateEditField('sales_cv', e.target.value)}
                                  className="w-20 text-right tabular-nums"
                                  data-testid={`edit-sales-cv-${mapping.mapping_id}`} />
                              </td>
                              <td className="py-3 px-3 text-gray-700">{new Date(mapping.updated_at).toLocaleDateString()}</td>
                            </>
                          ) : (
                            <>
                              <td className="py-3 px-3">
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${getFunnelColor(mapping.funnel_stage)}`}>
                                  {mapping.funnel_stage}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-right tabular-nums">{cellNum(mapping.spends_awareness_pct)}</td>
                              <td className="py-3 px-2 text-right tabular-nums">{cellNum(mapping.spends_conversion_pct)}</td>
                              <td className="py-3 px-2 text-right tabular-nums">{cellNum(mapping.sales_awareness_pct)}</td>
                              <td className="py-3 px-2 text-right tabular-nums">{cellNum(mapping.sales_conversion_pct)}</td>
                              <td className="py-3 px-3 text-gray-700">{new Date(mapping.updated_at).toLocaleDateString()}</td>
                            </>
                          )}

                          <td className="py-3 px-3 text-right">
                            {isUnmapped ? (
                              <span className="text-xs text-gray-400">—</span>
                            ) : isEditing ? (
                              <div className="flex items-center justify-end gap-2">
                                <button onClick={() => saveEditMapping(mapping.mapping_id)} className="text-[#32B4AC] hover:text-[#2a9d95]" data-testid={`save-edit-${mapping.mapping_id}`}>
                                  <Check className="w-4 h-4" />
                                </button>
                                <button onClick={() => { setEditingMapping(null); setEditForm(null); }} className="text-gray-400 hover:text-gray-600">
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                <button onClick={() => startEditMapping(mapping)} className="text-gray-400 hover:text-[#32B4AC] transition-colors" data-testid={`edit-${mapping.mapping_id}`}>
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteMapping(mapping.mapping_id)} className="text-gray-400 hover:text-red-600 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>

        {/* Bulk Map Dialog */}
        {showBulkDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowBulkDialog(false)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="bulk-map-dialog">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                <div>
                  <h2 className="text-lg font-bold text-[#0F0F0F]">Bulk Map Campaigns</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Select campaigns and assign them to a funnel stage</p>
                </div>
                <button onClick={() => setShowBulkDialog(false)} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
              </div>

              {/* Stage selector + controls */}
              <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-4 flex-wrap shrink-0">
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-semibold text-gray-600 whitespace-nowrap">Map to:</Label>
                  <Select value={bulkStage} onValueChange={setBulkStage}>
                    <SelectTrigger className="w-[160px] h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FUNNEL_STAGES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="bulk-overwrite" checked={bulkOverwrite} onChange={e => setBulkOverwrite(e.target.checked)} className="rounded" />
                  <label htmlFor="bulk-overwrite" className="text-xs text-gray-600">Overwrite existing</label>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={selectAllVisible} className="text-xs text-[#32B4AC] hover:underline" data-testid="select-all-btn">
                    {filteredBulkCampaigns.length > 0 && filteredBulkCampaigns.every(c => bulkSelected.has(c)) ? 'Deselect All' : 'Select All'}
                  </button>
                  <span className="text-xs text-gray-400">{bulkSelected.size} selected</span>
                </div>
              </div>

              {/* Search */}
              <div className="px-6 py-2 border-b border-gray-100 shrink-0">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} placeholder="Search campaigns..."
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-[#32B4AC]" data-testid="bulk-search" />
                </div>
              </div>

              {/* Campaign list */}
              <div className="flex-1 overflow-y-auto px-2 py-1">
                {filteredBulkCampaigns.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    {allCampaigns.length === 0 ? 'No campaigns uploaded yet. Upload data first.' : 'No campaigns match your search.'}
                  </div>
                ) : (
                  filteredBulkCampaigns.map(campaign => {
                    const isMapped = mappedCampaigns.has(campaign);
                    const isSelected = bulkSelected.has(campaign);
                    return (
                      <button key={campaign} onClick={() => toggleBulkSelect(campaign)}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-colors ${isSelected ? 'bg-[#32B4AC]/10' : 'hover:bg-gray-50'}`}
                        data-testid={`bulk-campaign-${campaign}`}>
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-[#32B4AC] border-[#32B4AC]' : 'border-gray-300'}`}>
                          {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span className="text-sm text-gray-800 font-mono truncate flex-1">{campaign}</span>
                        {isMapped && <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">already mapped</span>}
                      </button>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between shrink-0">
                <div className="text-xs text-gray-500">
                  {allCampaigns.length} campaigns available &middot; {mappedCampaigns.size} already mapped
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
                  <Button size="sm" className="bg-[#32B4AC] hover:bg-[#2a9d95]" onClick={handleBulkMap} disabled={bulkLoading || bulkSelected.size === 0} data-testid="bulk-map-submit">
                    {bulkLoading ? 'Mapping...' : `Map ${bulkSelected.size} Campaign${bulkSelected.size !== 1 ? 's' : ''} to ${bulkStage}`}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
